import type { Moment } from 'moment'
import moment from 'moment'
import qs from 'qs'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { DatesInputTrigger } from './Common/DatesInputTrigger'
import { GuestsInputTrigger } from './Common/GuestsInputTrigger'
import { PlaceInputTrigger } from './Common/PlaceInputTrigger'
import SearchAirports from './SearchAirports'
import SearchDatePicker from './SearchDatePicker'
import SearchOccupancyPicker from './SearchOccupancyPicker'
import useDefaultDates from '../Common/Hooks/useDefaultDates'

import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FareDropdown from 'components/Flights/FlightsSearch/SearchWidget/FareDropdown/FareDropdown'
import type { FlightDropdownOption } from 'components/Flights/types'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { FareType } from 'constants/flight'
import { FlightsFareTypes, fareClassToUrlMap } from 'constants/flight'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import useNavigateTo from 'hooks/useNavigateTo'
import {
  countOccupantsForFlights,
  isValidFlightsOccupants,
} from 'lib/offer/occupancyUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import {
  FLIGHTS_SEARCH_DAY_MAX_ADVANCE,
  FLIGHTS_SEARCH_DAY_MIN_ADVANCE,
} from 'tripPlanner/config/flights'
import { useTripMetadataPrefetch, useTrip } from 'tripPlanner/hooks/api/trip'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'

interface Props {
  tripId: string
  defaultDate?: string
  prefillData?: {
    departureAirport: App.AirportLocation | undefined
    arrivalAirport: App.AirportLocation | undefined
    fareType?: FareType
    checkinDate?: Moment
    checkoutDate?: Moment
  }
}

function SearchFlights({ tripId, prefillData, defaultDate }: Props) {
  const intl = useIntl()
  const { fareTypes, fareCabins } = useFlightFareLabels()
  useTripMetadataPrefetch({ tripId })

  const { data: trip } = useTrip({ tripId })

  const navigateTo = useNavigateTo()

  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const { flights, fareCabin, fareType, occupants } = useContext(
    FlightSearchWidgetStateContext,
  )

  const {
    id: flightId,
    checkinDate,
    checkoutDate,
    departureAirport,
    arrivalAirport,
    showDepartureError,
    showDestinationError,
    showSameAirportError,
    showTravelDatesError,
  } = flights[0]

  const flightsDispatch = useContext(FlightSearchWidgetDispatchContext)

  const [isAirportSelectDirty, setIsAirportSelectDirty] = useState(
    !!departureAirport && !!arrivalAirport,
  )
  const [isDatesDirty, setIsDatesDirty] = useState(
    !!checkinDate && !!checkoutDate,
  )

  // Same logic as in components\Flights\FlightsSearch\SearchWidget\DateSelectInput\DateSelectInput.tsx
  const minSearchDate = moment().add(FLIGHTS_SEARCH_DAY_MIN_ADVANCE, 'day')
  const maxSearchDate = moment().add(FLIGHTS_SEARCH_DAY_MAX_ADVANCE, 'day')

  const defaultDates = useDefaultDates({
    activeDate: defaultDate,
    tripStartDate: trip?.startDate,
    tripEndDate: trip?.endDate,
    minDate: minSearchDate,
    maxDate: maxSearchDate,
  })

  useEffect(() => {
    if (
      checkinDate ||
      checkoutDate ||
      !defaultDates.start ||
      !defaultDates.end
    ) {
      return
    }
    flightsDispatch({
      type: FlightSearchWidgetActions.SET_CHECKIN_DATE,
      id: flightId,
      date: defaultDates.start,
    })
    flightsDispatch({
      type: FlightSearchWidgetActions.SET_CHECKOUT_DATE,
      id: flightId,
      date: defaultDates.end,
    })
  }, [checkinDate, checkoutDate, flightsDispatch, defaultDates, flightId])

  const isOccupantsValid = useMemo(
    () => isValidFlightsOccupants(occupants),
    [occupants],
  )

  const canSearch =
    !showDepartureError &&
    !showDestinationError &&
    !showSameAirportError &&
    !showTravelDatesError &&
    isOccupantsValid

  const invalidDateSelection = useMemo(() => {
    if (fareType?.value === 'return') {
      return !checkinDate || !checkoutDate
    } else {
      return !checkinDate
    }
  }, [fareType, checkinDate, checkoutDate])

  const onSetDates = useCallback(
    (startDate?: Moment, endDate?: Moment) => {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_CHECKIN_DATE,
        date: startDate || null,
        id: flightId,
      })
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_CHECKOUT_DATE,
        date: endDate || null,
        id: flightId,
      })
    },
    [flightsDispatch, flightId],
  )

  const onSetOccupancies = useCallback(
    (occupancies: Array<App.Occupants>) => {
      // Flights should only have 1 occupants object
      const occupants = occupancies[0]

      flightsDispatch({
        type: FlightSearchWidgetActions.SET_OCCUPANTS,
        occupants,
      })
    },
    [flightsDispatch],
  )

  useEffect(() => {
    if (!departureAirport?.airportCode) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_DEPARTURE_ERROR,
        id: flightId,
        error: true,
      })
    } else {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_DEPARTURE_ERROR,
        id: flightId,
        error: false,
      })
    }

    if (!arrivalAirport?.airportCode) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_ARRIVAL_ERROR,
        id: flightId,
        error: true,
      })
    } else {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_ARRIVAL_ERROR,
        id: flightId,
        error: false,
      })
    }

    if (invalidDateSelection) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_TRAVEL_DATES_ERROR,
        id: flightId,
        error: true,
      })
    } else {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_TRAVEL_DATES_ERROR,
        id: flightId,
        error: false,
      })
    }

    if (departureAirport?.airportCode === arrivalAirport?.airportCode) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_SAME_AIRPORT_ERROR,
        id: flightId,
        error: true,
      })
    } else {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_SAME_AIRPORT_ERROR,
        id: flightId,
        error: false,
      })
    }
  }, [
    departureAirport?.airportCode,
    arrivalAirport?.airportCode,
    invalidDateSelection,
    flightsDispatch,
    flightId,
  ])

  useEffect(() => {
    if (prefillData?.departureAirport && !departureAirport) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT,
        id: flightId,
        airport: prefillData.departureAirport,
      })
    }

    if (prefillData?.arrivalAirport && !arrivalAirport) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT,
        id: flightId,
        airport: prefillData.arrivalAirport,
      })
    }

    if (prefillData?.checkinDate && !checkinDate) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_CHECKIN_DATE,
        id: flightId,
        date: prefillData?.checkinDate,
      })
    }

    if (prefillData?.checkoutDate && !checkoutDate) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_CHECKOUT_DATE,
        id: flightId,
        date: prefillData.checkoutDate,
      })
    }
  }, [
    arrivalAirport,
    checkinDate,
    checkoutDate,
    departureAirport,
    flightsDispatch,
    fareType,
    prefillData,
    flightId,
  ])

  useEffect(() => {
    if (prefillData?.fareType) {
      const prefilledFareType = fareTypes.find(
        (t) => !t.disabled && t.value === prefillData.fareType,
      )
      if (prefilledFareType) {
        flightsDispatch({
          type: FlightSearchWidgetActions.SET_FARE_TYPE,
          fareType: prefilledFareType,
        })
      }
    }
  }, [flightsDispatch, prefillData?.fareType, fareTypes])

  const changeFareType = useCallback(
    (fareType: FlightDropdownOption) => {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_FARE_TYPE,
        fareType,
      })
    },
    [flightsDispatch],
  )

  const changeFareCabin = useCallback(
    (fareCabin: FlightDropdownOption) => {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_FARE_CABIN,
        fareCabin,
      })
    },
    [flightsDispatch],
  )
  const source = useTripClientSource()
  const onSearchAirport = () => {
    fireInteractionEvent(addToTripClickEvent('modal_flights_search'))
    const transformedOccupants = countOccupantsForFlights([occupants])
    const isReturnFare = fareType?.value === FlightsFareTypes.RETURN

    const queryParams = {
      originAirportCode: departureAirport?.airportCode,
      originAirportName: departureAirport?.airportName,
      destinationAirportCode: arrivalAirport?.airportCode,
      destinationAirportName: arrivalAirport?.airportName,
      departDate: moment(checkinDate).format(ISO_DATE_FORMAT),
      returnDate: isReturnFare ?
          moment(checkoutDate).format(ISO_DATE_FORMAT) :
        null,
      adults: transformedOccupants.adults,
      children: transformedOccupants.children,
      infants: transformedOccupants.infants,
      childrenAge: transformedOccupants.childrenAge?.join(','),
      fareClass: fareCabin?.value ? fareClassToUrlMap[fareCabin.value] : undefined,
      fareType: fareType?.value,
      searchId: moment().unix(),
      source,
      // carrier: isFlightsCredit ? creditDetails?.carrier : undefined,
      // flightsCredit: isFlightsCredit || undefined,
    }

    const query = qs.stringify(queryParams)

    navigateTo('/flights-search-results', query)
  }

  const isMobile = useScreenSizeOnly('mobile')

  const modalTitle = intl.formatMessage({
    id: 'wCS-WzzrIvCkAZcvuHho',
    defaultMessage: 'Search flights',
    description: 'Modal title for flight search in trip planner in the trip planner item editor modal',
  })

  const fromLabel = intl.formatMessage({
    id: 'vQk7R2j4Xzg4E5a8X7TX',
    defaultMessage: 'From',
    description: 'Label for departure airport selection in flight search',
  })

  const toLabel = intl.formatMessage({
    id: 'yusBEpm5f84JrUpXRj23',
    defaultMessage: 'To',
    description: 'Label for arrival airport selection in flight search',
  })

  const selectAirportLabel = intl.formatMessage({
    id: 'FUuHQ9TXjNusOsddj-gJ',
    defaultMessage: 'Select an airport',
    description: 'Default placeholder when no airport is selected in flight search',
  })

  const departingLabel = intl.formatMessage({
    id: '8i2JeAUG9F3owOUe4dFg',
    defaultMessage: 'Departing',
    description: 'Label for departure date in flight search date picker trigger',
  })

  const returningLabel = intl.formatMessage({
    id: 'hzaR1f4TdEktsbuL2M_g',
    defaultMessage: 'Returning',
    description: 'Label for return date in flight search date picker trigger',
  })

  const selectDateLabel = intl.formatMessage({
    id: '4Pdta8RoqlbO6si_2uXx',
    defaultMessage: 'Select date',
    description: 'Default placeholder when no date is selected in flight search',
  })

  const departLabel = intl.formatMessage({
    id: 'WqvVvMlFIdWYB6o1dNA2',
    defaultMessage: 'Depart',
    description: 'Label for departure date in flight search date range picker',
  })

  const returnLabel = intl.formatMessage({
    id: 'hgO4w9lROA5bePGl69Bz',
    defaultMessage: 'Return',
    description: 'Label for return date in flight search date range picker',
  })

  const travellersLabel = intl.formatMessage({
    id: 'xZIeTTHuUtw8UnsmWfB_',
    defaultMessage: 'Travellers',
    description: 'Label for travellers/passengers selection in flight search',
  })

  const sameAirportError = intl.formatMessage({
    id: 'YHp7AAgg2D9ELxCIavgl',
    defaultMessage: 'Please select different departure & arrival airports.',
    description: 'Error message when same airport selected for departure and arrival',
  })

  const datesError = intl.formatMessage({
    id: 'jLq9XPkF4Y87RsgTYrLc',
    defaultMessage: 'Make sure you select proper dates.',
    description: 'Error message when flight travel dates are invalid',
  })

  const departureError = intl.formatMessage({
    id: 'ayVJFapmDPl-6Wlrd6Uj',
    defaultMessage: 'Please select another departure airport.',
    description: 'Error message when departure airport selection is invalid',
  })

  const destinationError = intl.formatMessage({
    id: 'qkoK48vCFMjAmRp3o6RF',
    defaultMessage: 'Please select another destination airport.',
    description: 'Error message when destination airport selection is invalid',
  })

  const searchButtonLabel = intl.formatMessage({
    id: 'RoEaHGcSDM3h0-MJKNon',
    defaultMessage: 'Search',
    description: 'Button text to submit flight search in trip planner',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <VerticalSpacer gap={12}>
              <div>
                <FareDropdown
                  options={fareTypes}
                  onOptionChange={changeFareType}
                  selectedOption={fareType}
                />
                <FareDropdown
                  options={fareCabins}
                  selectedOption={fareCabin}
                  onOptionChange={changeFareCabin}
                />
              </div>
              <PlaceInputTrigger
                title={fromLabel}
                subTitle={departureAirport?.airportName || selectAirportLabel}
                onClick={() => {
                  setIsAirportSelectDirty(true)
                  openModal(SearchAirports, {
                    tripId,
                    airportType: 'departure' as const,
                    flightId,
                  })
                }}
              />
              <PlaceInputTrigger
                title={toLabel}
                subTitle={arrivalAirport?.airportName || selectAirportLabel}
                onClick={() => {
                  setIsAirportSelectDirty(true)
                  openModal(SearchAirports, {
                    tripId,
                    airportType: 'arrival' as const,
                    flightId,
                  })
                }}
              />
              <DatesInputTrigger
                title1={departingLabel}
                title2={returningLabel}
                subTitle1={selectDateLabel}
                subTitle2={selectDateLabel}
                checkinDate={checkinDate}
                checkoutDate={checkoutDate}
                onClick={() => {
                  setIsDatesDirty(true)
                  openModal(SearchDatePicker, {
                    onSetDates,
                    minDate: minSearchDate,
                    maxDate: maxSearchDate,
                    initStartDate: checkinDate,
                    initEndDate: checkoutDate,
                    initVisibleDate:
                      !checkinDate && !checkoutDate ?
                        trip?.startDate :
                        undefined,
                    useSingleDate: fareType?.value === FlightsFareTypes.ONE_WAY,
                    startLabel: fareType?.value === FlightsFareTypes.RETURN ? departLabel : undefined,
                    endLabel: fareType?.value === FlightsFareTypes.RETURN ? returnLabel : undefined,
                  })
                }}
                useSingleDate={fareType?.value === FlightsFareTypes.ONE_WAY}
              />
              <GuestsInputTrigger
                occupancies={[occupants]}
                title={travellersLabel}
                isFlights
                onClick={() =>
                  openModal(SearchOccupancyPicker, {
                    isFlights: true,
                    tripId,
                    initOccupants: [occupants],
                    onSetOccupancies,
                  })}
              />
            </VerticalSpacer>
            {isAirportSelectDirty && showSameAirportError && (
              <MessageBanner kind="critical">{sameAirportError}</MessageBanner>
            )}
            {isDatesDirty && showTravelDatesError && (
              <MessageBanner kind="critical">{datesError}</MessageBanner>
            )}
            {isAirportSelectDirty && showDepartureError && (
              <MessageBanner kind="critical">{departureError}</MessageBanner>
            )}
            {isAirportSelectDirty && showDestinationError && (
              <MessageBanner kind="critical">{destinationError}</MessageBanner>
            )}
            <Group direction="horizontal-reverse">
              <TextButton
                kind="primary"
                fit={isMobile ? 'full-width' : 'auto'}
                onClick={onSearchAirport}
                disabled={!canSearch}
                size="large"
              >
                {searchButtonLabel}
              </TextButton>
            </Group>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SearchFlights
